<template>
  <div class="d-flex flex-column flex-grow-1 rounded p-3">
    <BaseToolBar title="Détail du véhicule" :subtitle="vehiculeTitle">
      <router-link to="/vehicules" class="btn btn-secondary mr-2">
        Retour à la liste
      </router-link>
    </BaseToolBar>

    <div v-if="loading && !vehicule.id" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Chargement...</span>
      </div>
      <p class="mt-2">Chargement des données du véhicule...</p>
    </div>

    <div v-else class="mt-3">
      <div class="mb-3">
        <div class="d-flex">
          <div
            class="nav flex-column nav-pills mr-4"
            style="min-width: 200px;"
            role="tablist"
          >
            <a
              class="nav-link"
              :class="{ active: tab === 'vehicule' }"
              @click.prevent="changeTab('vehicule')"
              href="#"
              role="tab"
            >
              <i class="fas fa-car mr-2"></i>Véhicule
            </a>
            <a
              class="nav-link"
              :class="{ active: tab === 'contrat' }"
              @click.prevent="changeTab('contrat')"
              href="#"
              role="tab"
            >
              <i class="fas fa-file-contract mr-2"></i>Contrat
            </a>
            <a
              class="nav-link"
              :class="{ active: tab === 'affect' }"
              @click.prevent="changeTab('affect')"
              href="#"
              role="tab"
            >
              <i class="fas fa-users mr-2"></i>Affectations
              <span
                v-if="
                  vehicule.collaborateurs && vehicule.collaborateurs.length > 0
                "
                class="badge ml-1 badge-primary"
              >
                {{ vehicule.collaborateurs.length }}
              </span>
            </a>
            <a
              class="nav-link"
              :class="{ active: tab === 'entretiens' }"
              @click.prevent="changeTab('entretiens')"
              href="#"
              role="tab"
            >
              <i class="fas fa-wrench mr-2"></i>Entretiens
              <span
                v-if="vehicule.entretiens && vehicule.entretiens.length > 0"
                class="badge ml-1"
                :class="hasExpiredEntretien ? 'badge-danger' : 'badge-primary'"
              >
                {{ vehicule.entretiens.length }}
              </span>
            </a>
            <a
              class="nav-link"
              :class="{ active: tab === 'equipements' }"
              @click.prevent="changeTab('equipements')"
              href="#"
              role="tab"
            >
              <i class="fas fa-tools mr-2"></i>Équipements
              <span
                v-if="vehicule.equipements && vehicule.equipements.length > 0"
                class="badge badge-info ml-1"
              >
                {{ vehicule.equipements.length }}
              </span>
            </a>
            <a
              class="nav-link"
              :class="{ active: tab === 'sinistres' }"
              @click.prevent="changeTab('sinistres')"
              href="#"
              role="tab"
            >
              <i class="fas fa-car-crash mr-2"></i>Sinistres
              <span
                v-if="vehicule.sinistres && vehicule.sinistres.length > 0"
                class="badge badge-warning ml-1"
              >
                {{ vehicule.sinistres.length }}
              </span>
            </a>
            <a
              class="nav-link"
              :class="{ active: tab === 'contraventions' }"
              @click.prevent="changeTab('contraventions')"
              href="#"
              role="tab"
            >
              <i class="fas fa-exclamation-triangle mr-2"></i>Contraventions
              <span
                v-if="
                  vehicule.contraventions && vehicule.contraventions.length > 0
                "
                class="badge ml-1"
                :class="
                  hasContraventionsEnAttente ? 'badge-warning' : 'badge-success'
                "
              >
                {{ vehicule.contraventions.length }}
              </span>
            </a>
          </div>

          <div class="tab-content flex-grow-1 p-3">
            <div v-show="tab === 'vehicule'" class="tab-pane fade show active">
              <base-form
                @click="updateVehicule(vehicule)"
                :statut="statut"
                :loading="loading"
                @delete="deleteVehicule(vehicule)"
                deleteBtn
              >
                <base-form-row row>
                  <base-input
                    v-model="vehicule.immatriculation"
                    inputText="Immatriculation"
                    :errors="feedback.immatriculation"
                  ></base-input>
                  <base-input
                    v-model="vehicule.modele"
                    inputText="Modèle"
                    :errors="feedback.modele"
                  ></base-input>
                </base-form-row>
                <base-form-row row>
                  <base-input
                    v-model="vehicule.puissance_fiscale"
                    inputText="Puissance fiscale P6"
                    :errors="feedback.puissance_fiscale"
                  ></base-input>
                  <base-input
                    v-model="vehicule.puissance_dyn"
                    inputText="Puissance DYN P2"
                    :errors="feedback.puissance_dyn"
                  ></base-input>
                </base-form-row>
                <base-form-row row>
                  <base-input
                    v-model="vehicule.valeur_vehicule"
                    inputText="Valeur du véhicule"
                    :errors="feedback.valeur_vehicule"
                  ></base-input>
                  <base-input
                    v-model="vehicule.valeur_carbone"
                    inputText="Prix carbone"
                    :errors="feedback.valeur_carbone"
                  ></base-input>
                </base-form-row>
                <base-form-row row>
                  <base-inputDatePicker
                    v-model="vehicule.date_immatriculation"
                    inputText="Date de première immatriculation"
                    :errors="feedback.date_immatriculation"
                  ></base-inputDatePicker>
                </base-form-row>
                <div class="pt-4">
                  <uploader
                    v-if="vehicule && vehicule.id"
                    :id="'doc_vehicule_' + vehicule.id"
                    url="/upload/document"
                    file_system="VEHICULES"
                    file_field="vehicule"
                    :file_key="vehicule.id"
                    title="Documents véhicule"
                  ></uploader>
                </div>
              </base-form>
            </div>

            <div v-show="tab === 'contrat'" class="tab-pane fade show active">
              <base-form
                @click="updateVehicule(vehicule)"
                :statut="statut"
                :loading="loading"
                @delete="deleteVehicule(vehicule)"
                deleteBtn
              >
                <base-form-row row>
                  <base-select
                    inputText="Catégorie"
                    v-model.number="vehicule.categorie_id"
                    :options="data.categories"
                    :errors="feedback.categorie_id"
                  ></base-select>
                  <base-input
                    v-model="vehicule.prestataire"
                    inputText="Prestataire"
                    :errors="feedback.prestataire"
                  ></base-input>
                </base-form-row>
                <base-form-row row>
                  <base-select
                    inputText="Société contractante"
                    v-model.number="vehicule.societe_id"
                    :options="data.societes"
                    :errors="feedback.societe_id"
                  ></base-select>
                </base-form-row>
                <base-form-row row>
                  <base-input
                    v-model="vehicule.num_contrat"
                    inputText="Numéro de contrat"
                    :errors="feedback.num_contrat"
                  ></base-input>
                  <base-input
                    v-model="vehicule.nb_km"
                    inputText="Kilomètres prévus"
                    :errors="feedback.nb_km"
                  ></base-input>
                </base-form-row>
                <base-form-row row>
                  <base-inputDatePicker
                    v-model="vehicule.debut_contrat"
                    inputText="Date de début de contrat"
                    :errors="feedback.debut_contrat"
                  ></base-inputDatePicker>
                  <base-inputDatePicker
                    v-model="vehicule.fin_contrat"
                    inputText="Date de fin de contrat"
                    :errors="feedback.fin_contrat"
                  ></base-inputDatePicker>
                </base-form-row>

                <base-form-row row>
                  <base-inputDatePicker
                    v-model="vehicule.date_restitution"
                    inputText="Date de restitution"
                    :errors="feedback.date_restitution"
                  ></base-inputDatePicker>
                </base-form-row>
                <base-form-row row>
                  <base-input
                    v-model="vehicule.loyer_ht"
                    inputText="Loyer HT"
                    :errors="feedback.loyer_ht"
                  ></base-input>
                  <base-input
                    v-model="vehicule.loyer_ttc"
                    inputText="Loyer TTC"
                    :errors="feedback.loyer_ttc"
                  ></base-input>
                </base-form-row>
                <base-form-row row>
                  <base-select
                    inputText="Assurance"
                    v-model.number="vehicule.assurance_id"
                    :options="data.assurances"
                    :errors="feedback.assurance_id"
                  ></base-select>
                  <base-input
                    v-model="vehicule.tarif_assurance"
                    inputText="Tarif assurance"
                    :errors="feedback.tarif_assurance"
                  ></base-input>
                </base-form-row>
                <div class="pt-4">
                  <uploader
                    v-if="vehicule && vehicule.id"
                    :id="'doc_vehicule_contrat_' + vehicule.id"
                    url="/upload/document"
                    file_system="VEHICULES"
                    file_field="contrat"
                    :file_key="vehicule.id"
                    title="Documents contrat"
                  ></uploader>
                </div>
              </base-form>
            </div>

            <div v-show="tab === 'affect'" class="tab-pane fade show active">
              <!-- Remplacé par le composant VehiculeAffectations -->
              <vehicule-affectations
                :vehicule="vehicule"
                :data="data"
                :statut="statut"
                :loading="loading"
                @update-vehicule="updateVehicule"
                @delete-affectation="handleDeleteAffectation"
                @delete-affectation-etablissement="
                  handleDeleteAffectationEtablissement
                "
              />
            </div>
            <div
              v-show="tab === 'entretiens'"
              class="tab-pane fade show active"
            >
              <vehicule-entretien
                :vehicule="vehicule"
                :statut="statut"
                :loading="loading"
              />
            </div>
            <div
              v-show="tab === 'equipements'"
              class="tab-pane fade show active"
            >
              <vehicule-equipement
                :vehicule="vehicule"
                :statut="statut"
                :loading="loading"
              />
            </div>
            <div v-show="tab === 'sinistres'" class="tab-pane fade show active">
              <vehicule-sinistres
                :vehicule="vehicule"
                :statut="statut"
                :loading="loading"
              />
            </div>
            <div
              v-show="tab === 'contraventions'"
              class="tab-pane fade show active"
            >
              <vehicule-contraventions
                :vehicule="vehicule"
                :statut="statut"
                :loading="loading"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseForm from "@/components/bases/Form.vue";
import BaseFormRow from "@/components/bases/FormRow.vue";
import BaseInput from "@/components/bases/Input.vue";
import BaseSelect from "@/components/bases/Select.vue";
import BaseInputDatePicker from "@/components/bases/InputDatePicker.vue";
import Uploader from "@/components/bases/Uploader.vue";
import VehiculeAffectations from "@/components/vehicules/VehiculeAffectations.vue";
import VehiculeSinistres from "@/components/vehicules/VehiculeSinistres.vue";
import VehiculeEquipement from "@/components/vehicules/VehiculeEquipements.vue";
import VehiculeEntretien from "@/components/vehicules/VehiculeEntretiens.vue";
import VehiculeContraventions from "@/components/vehicules/VehiculeContraventions.vue";

export default {
  name: "VehiculeDetail",
  components: {
    BaseToolBar,
    BaseForm,
    BaseFormRow,
    BaseInput,
    BaseSelect,
    BaseInputDatePicker,
    Uploader,
    VehiculeAffectations,
    VehiculeSinistres,
    VehiculeEquipement,
    VehiculeEntretien,
    VehiculeContraventions
  },
  props: {
    id: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      vehicule: {
        id: null,
        immatriculation: "",
        modele: "",
        puissance_fiscale: "",
        puissance_dyn: "",
        valeur_vehicule: "",
        valeur_carbone: "",
        date_immatriculation: null,
        prestataire: "",
        date_contrat: null,
        num_contrat: "",
        mensualite: 0,
        frais_km: 0,
        kms_inclus: 0,
        duree: 0,
        date_restitution: null,
        classeur: "",
        categorie_id: null,
        collaborateurs: [],
        etablissements: [],
        sinistres: [],
        equipements: [],
        entretiens: [],
        contraventions: []
      },
      data: {
        categories: [],
        categoriesAffectation: [],
        etablissements: []
      },
      feedback: {},
      loading: false,
      statut: null,
      tab: "vehicule"
    };
  },
  computed: {
    vehiculeTitle() {
      if (this.vehicule.immatriculation && this.vehicule.modele) {
        return `${this.vehicule.immatriculation} - ${this.vehicule.modele}`;
      } else if (this.vehicule.immatriculation) {
        return this.vehicule.immatriculation;
      } else if (this.vehicule.modele) {
        return this.vehicule.modele;
      } else {
        return "Nouveau véhicule";
      }
    },
    hasExpiredEntretien() {
      if (!this.vehicule.entretiens || this.vehicule.entretiens.length === 0)
        return false;

      return this.vehicule.entretiens.some(entretien => {
        if (!entretien.date_fin_validite) return false;
        return this.$moment().isAfter(
          this.$moment(entretien.date_fin_validite)
        );
      });
    },
    hasContraventionsEnAttente() {
      if (
        !this.vehicule.contraventions ||
        this.vehicule.contraventions.length === 0
      )
        return false;

      return this.vehicule.contraventions.some(contravention => {
        return parseInt(contravention.statut) < 3;
      });
    }
  },
  methods: {
    loadDataAndSetTabFromUrl() {
      // Charger les données principales
      this.loading = true;

      Promise.all([
        this.loadData(),
        this.id ? this.loadVehicule() : Promise.resolve()
      ])
        .catch(error => {
          console.error("Erreur lors du chargement:", error);
          this.$toast.error("Erreur lors du chargement des données");
        })
        .finally(() => {
          this.loading = false;
          // Après le chargement, définir l'onglet à partir de l'URL
          this.setTabFromUrl();
        });
    },
    setTabFromUrl() {
      // Récupérer le paramètre tab de l'URL
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get("tab");

      // Les onglets valides dans votre application
      const validTabs = [
        "vehicule",
        "contrat",
        "affect",
        "sinistres",
        "entretiens",
        "contraventions"
      ];

      // Si le paramètre tab existe et est valide, l'utiliser
      if (tabParam && validTabs.includes(tabParam)) {
        this.tab = tabParam;
      }
    },
    changeTab(newTab) {
      this.tab = newTab;

      // Mettre à jour l'URL sans recharger la page
      const url = new URL(window.location);
      url.searchParams.set("tab", newTab);
      window.history.pushState({}, "", url);
    },
    loadVehicule() {
      this.loading = true;
      this.$http
        .get(`/vehicules/${this.id}`)
        .then(response => {
          this.vehicule = response.data;
          if (!this.vehicule.collaborateurs) this.vehicule.collaborateurs = [];
          if (!this.vehicule.etablissements) this.vehicule.etablissements = [];
          if (!this.vehicule.sinistres) this.vehicule.sinistres = [];
          if (!this.vehicule.equipements) this.vehicule.equipements = [];
          if (!this.vehicule.entretiens) this.vehicule.entretiens = [];
          if (!this.vehicule.contraventions) this.vehicule.contraventions = [];
        })
        .catch(error => {
          console.error(error);
          this.$router.push("/vehicules");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadData() {
      return this.$http
        .get("/vehicules-data")
        .then(resp => {
          this.data = resp.data;
        })
        .catch(error => {
          console.error("Erreur lors du chargement des données :", error);
        });
    },
    updateVehicule(vehicule) {
      this.feedback = {};
      this.loading = true;
      this.$http
        .put(`/vehicules/${vehicule.id}`, vehicule)
        .then(resp => {
          this.vehicule = resp.data;
          this.statut = "success";
          setTimeout(() => {
            this.statut = null;
          }, 2000);
        })
        .catch(error => (this.feedback = errHandler(error)))
        .finally(() => (this.loading = false));
    },
    deleteVehicule(vehicule) {
      if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce véhicule ?"))
        return;

      this.feedback = {};
      this.loading = true;
      this.$http
        .delete(`/vehicules/${vehicule.id}`)
        .then(() => {
          this.$router.push("/vehicules");
        })
        .catch(error => (this.feedback = errHandler(error)))
        .finally(() => (this.loading = false));
    },
    handleDeleteAffectation(affectation) {
      this.loading = true;
      this.$http
        .delete(`/vehicules-affectation/${affectation.id}`)
        .then(() => {
          const index = this.vehicule.collaborateurs.findIndex(
            x => x.id === affectation.id
          );
          if (index !== -1) {
            this.vehicule.collaborateurs.splice(index, 1);
          }
        })
        .catch(error => (this.feedback = errHandler(error)))
        .finally(() => (this.loading = false));
    },
    handleDeleteAffectationEtablissement(affectation) {
      this.loading = true;
      this.$http
        .delete(`/vehicules-affectation-etab/${affectation.id}`)
        .then(() => {
          const index = this.vehicule.etablissements.findIndex(
            x => x.id === affectation.id
          );
          if (index !== -1) {
            this.vehicule.etablissements.splice(index, 1);
          }
        })
        .catch(error => (this.feedback = errHandler(error)))
        .finally(() => (this.loading = false));
    }
  },

  mounted() {
    // Chargement séquentiel pour s'assurer que les données sont disponibles
    this.loading = true;
    this.loadDataAndSetTabFromUrl();
    // D'abord charger les données de référence
    this.loadData()
      .then(() => {
        // Puis charger les données du véhicule
        return this.loadVehicule();
      })
      .finally(() => {
        this.loading = false;
      });
  },
  watch: {
    "$route.query": {
      handler(query) {
        if (query.tab && query.tab !== this.tab) {
          const validTabs = [
            "vehicule",
            "contrat",
            "affect",
            "sinistres",
            "entretiens",
            "contraventions"
          ];
          if (validTabs.includes(query.tab)) {
            this.tab = query.tab;
          }
        }
      },
      deep: true
    }
  }
};
</script>

<style lang="css">
.nav-pills .nav-link {
  color: #495057;
  background-color: #f8f9fa;
  margin-bottom: 5px;
  cursor: pointer;
}
.nav-pills .nav-link.active {
  color: #fff;
  background-color: #007bff;
}
.tab-content {
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}
</style>
